import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogInIcon as LogIn, EyeIcon as Eye, EyeOffIcon as EyeOff } from './icons';
import { usePermissions } from '../contexts/PermissionContext';
import { UserRole } from '../types/permissions';
import { mapPermissionIdToRole } from '../lib/permissionMapper';
import { login } from '../lib/auth';
import { sanitizeInput, validateInput, INPUT_VALIDATION } from '../config/security';
import { getSafeErrorMessage } from '../config';
import { Button, InlineAlert } from './ui';
import { getTenantLogo } from '../utils/getTenantLogo';
import { logger } from '../lib/terminalLogger';
import { useNotifications } from './feedback/NotificationProvider';
import { useConnectivity } from '../contexts/ConnectivityContext';
import { useCorelConnection } from '../hooks/useCorelConnection';
import { ConnectionIndicator } from './ConnectionIndicator';
import './LoginPage.css';
import { APIError } from '@/lib/api';
import { removeKey } from '@/utils/remove-sessionstorage';

// Chaves do localStorage para "lembrar-me"
const STORAGE_KEYS = {
  REMEMBERED_USERNAME: 'cdr:auth:rememberedUsername',
  REMEMBER_ME: 'cdr:auth:rememberMe',
} as const;

export function LoginPage() {
  const navigate = useNavigate();
  const [adminId, setAdminId] = useState(() => {
    const remember = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    return remember ? (localStorage.getItem(STORAGE_KEYS.REMEMBERED_USERNAME) ?? '') : '';
  });
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    const flag = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    const username = localStorage.getItem(STORAGE_KEYS.REMEMBERED_USERNAME);
    return flag && !!username;
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setRole } = usePermissions();
  const { notify } = useNotifications();

  // Conexões
  const { apiOnline, isChecking: apiChecking } = useConnectivity();
  const corelConnection = useCorelConnection();

  // Verifica se os campos têm mais de 4 caracteres para mostrar visual verde
  const adminIdHasContent = adminId.length > 4;
  const passwordHasContent = password.length > 4;

  // // Carrega dados salvos do "lembrar-me" ao montar o componente
  // useEffect(() => {
  //   try {
  //     const savedRememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  //     const savedUsername = localStorage.getItem(STORAGE_KEYS.REMEMBERED_USERNAME);

  //     logger.debug('[LOGIN] Verificando "lembrar-me":', { savedRememberMe, savedUsername: savedUsername ? '***' : null });

  //     if (savedRememberMe && savedUsername) {
  //       setRememberMe(true);
  //       setAdminId(savedUsername);
  //       logger.debug('[LOGIN] Dados de "lembrar-me" carregados com sucesso');
  //     }
  //   } catch (error) {
  //     logger.warn('Erro ao carregar dados de "lembrar-me":', error);
  //   }
  // }, []);

  // Salva ou remove dados do "lembrar-me" quando o checkbox muda
  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);

    try {
      if (checked) {
        // Salva apenas o username (nunca a senha por segurança)
        if (adminId.trim()) {
          localStorage.setItem(STORAGE_KEYS.REMEMBERED_USERNAME, adminId.trim());
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
          logger.debug('[LOGIN] "Lembrar-me" ativado para:', adminId.trim());
        } else {
          // Salva a preferência mesmo sem username - será preenchido quando digitar
          localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
          logger.debug('[LOGIN] "Lembrar-me" ativado (aguardando username)');
        }
      } else {
        // Remove dados salvos
        localStorage.removeItem(STORAGE_KEYS.REMEMBERED_USERNAME);
        localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
        logger.debug('[LOGIN] "Lembrar-me" desativado, dados removidos');
      }
    } catch (error) {
      logger.warn('Erro ao salvar dados de "lembrar-me":', error);
    }
  };

  // Atualiza o username salvo quando o usuário digita e o checkbox está marcado
  useEffect(() => {
    // Verifica se a flag está salva no localStorage (mais confiável que o state em alguns casos)
    const savedRememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';

    if ((rememberMe || savedRememberMe) && adminId.trim()) {
      try {
        localStorage.setItem(STORAGE_KEYS.REMEMBERED_USERNAME, adminId.trim());
        // Garantir que a flag também está salva
        localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
      } catch (error) {
        logger.warn('Erro ao atualizar username salvo:', error);
      }
    }
  }, [adminId, rememberMe]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await login(adminId, password);

      if (!response.success) {
        throw new Error("[LoginPage] Falha na autenticação. Integração com API de autenticação necessária.");
      }

      // Salva dados do "lembrar-me" após login bem-sucedido
      // IMPORTANTE: Só salva se marcado, NÃO remove automaticamente para evitar perda de dados
      // A remoção só acontece quando o usuário explicitamente desmarca o checkbox
      if (rememberMe && adminId.trim()) {
        localStorage.setItem(STORAGE_KEYS.REMEMBERED_USERNAME, adminId.trim());
        localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, 'true');
        logger.debug('[LOGIN] Dados de "lembrar-me" salvos para:', adminId.trim());
      }


      // Verifica se existem permissões na resposta e usa a primeira
      const userPermissions = response.data.permissionsConfig;
      const userData = response.data

      if (userPermissions?.length === 0) {
        notify({
          type: 'warning',
          title: 'Sem permissão!',
          message: "Esse Usuário não tem permissão",
          duration: 2000
        });
        return
      }

      //garante que o array de permissions está cheio
      if (userPermissions && userPermissions.length > 0) {
        // Usa mapeamento centralizado de permissões
        const permId = userPermissions[0]?.perm_id;
        if (!permId) {
          logger.error("Erro ao logar no painel")
        }
        const userRole = mapPermissionIdToRole(permId);

        //userPermission ISADMIN
        if (userData.isAdmin && permId === 2) {
          setRole('admin');
        }

        //se retornar null e uma permissão que nao está mapeada
        if (userRole === null) {
          return
        }
        setRole(userRole)
      }

      // else if (response.data.isAdmin) {
      //   setRole('admin');
      // } else {
      //   const fallbackRole = (response.data.usuario.tipo as UserRole) || 'cliente';
      //   setRole(fallbackRole);
      // }

      // ✅ Login bem-sucedido - navegar para main
      navigate('/main');
    } catch (err) {
      if (err instanceof APIError && [400, 404].includes(err.status)) {
        setError(err.data?.msg);
        notify({
          type: 'error',
          title: 'Falha na Autenticação',
          message: err.data?.msg,
          duration: 5000
        });
        return
      }
      // 🛡️ SEGURANÇA: Usar mensagem genérica em produção para não expor detalhes técnicos
      console.log(err)
      const errorMessage = getSafeErrorMessage(err, 'Falha na autenticação. Verifique suas credenciais.');
      setError(errorMessage);

      // Notificação toast para feedback visual adicional
      notify({
        type: 'error',
        title: 'Falha na Autenticação',
        message: errorMessage,
        duration: 5000
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 p-3 sm:p-4">
      <div className="w-full max-w-md bg-navy-800/95 backdrop-blur-sm rounded-xl shadow-2xl p-6 sm:p-8 border border-navy-700/50 animate-fade-in">
        <div className="space-y-6">
          {/* Logo Section */}
          <div className="flex justify-center items-center mb-6 sm:mb-8">
            <div className="relative">
              <img
                src={getTenantLogo()}
                alt="SysOnePrint"
                className="h-14 sm:h-16 w-auto object-contain transition-transform duration-300 hover:scale-105"
              />
            </div>
          </div>

          {/* Form Section */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Login Input */}
            <div className="space-y-2">
              <label
                htmlFor="adminId"
                className="block text-sm font-medium text-gray-300 transition-colors"
              >
                Login
              </label>
              <div className="relative">
                <input
                  id="adminId"
                  type="text"
                  value={adminId}
                  onChange={(e) => {
                    const sanitized = sanitizeInput(e.target.value, INPUT_VALIDATION.maxLengths.username);
                    if (validateInput(sanitized, 'username') || sanitized === '') {
                      setAdminId(sanitized);
                      setError(null);
                    }
                  }}
                  className={`w-full px-4 py-3 bg-navy-900/80 border rounded-lg text-white text-sm placeholder-gray-500 
                             transition-all duration-200 hover:border-navy-600
                             focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50
                             ${adminIdHasContent ? 'border-green-500/50 bg-green-500/5' : 'border-navy-700'}`}
                  placeholder="Digite seu login"
                  required
                  maxLength={INPUT_VALIDATION.maxLengths.username}
                  autoComplete="username"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-300 transition-colors"
              >
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    const sanitized = sanitizeInput(e.target.value, INPUT_VALIDATION.maxLengths.password);
                    setPassword(sanitized);
                    setError(null);
                  }}
                  className={`w-full px-4 py-3 pr-12 bg-navy-900/80 border rounded-lg text-white text-sm placeholder-gray-500 
                             transition-all duration-200 hover:border-navy-600
                             focus:outline-none focus:ring-2 focus:ring-gold-400/50 focus:border-gold-400/50
                             ${passwordHasContent ? 'border-green-500/50 bg-green-500/5' : 'border-navy-700'}`}
                  placeholder="Digite sua senha"
                  required
                  maxLength={INPUT_VALIDATION.maxLengths.password}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white 
                           transition-colors duration-200 p-1 rounded focus:outline-none focus:ring-2 
                           focus:ring-gold-400/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Remember Me Checkbox */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => handleRememberMeChange(e.target.checked)}
                disabled={isLoading}
                className="h-4 w-4 rounded border-navy-600 bg-navy-900/80 text-gold-400 
                         focus:ring-2 focus:ring-gold-400/50 focus:ring-offset-0 
                         transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label
                htmlFor="rememberMe"
                className="ml-3 block text-sm text-gray-300 cursor-pointer select-none 
                          transition-colors duration-200 hover:text-gray-200"
              >
                Lembrar-me
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="animate-slide-down">
                <InlineAlert
                  type="error"
                  message={error}
                  dismissible
                  onDismiss={() => setError(null)}
                />
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="warning"
              size="lg"
              loading={isLoading}
              icon={LogIn}
              iconPosition="left"
              className="w-full justify-center font-semibold shadow-lg hover:shadow-xl 
                       transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              disabled={isLoading}
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>

          {/* Connection Status - Posicionado abaixo do botão principal */}
          <div className="pt-2 flex items-center justify-center gap-5">
            {/* API Status */}
            <div className="flex items-center gap-1.5">
              <ConnectionIndicator
                status={apiOnline ? 'healthy' : (apiChecking ? 'validating' : 'disconnected')}
                size="sm"
              />
              <span className="text-xs text-gray-500 font-medium">API</span>
            </div>

            {/* Separator */}
            <div className="h-3 w-px bg-navy-700/50"></div>

            {/* Corel Status */}
            <div className="flex items-center gap-1.5">
              <ConnectionIndicator
                status={corelConnection.status}
                latencyMs={corelConnection.latencyMs}
                onClick={corelConnection.retry}
                size="sm"
              />
              <span className="text-xs text-gray-500 font-medium">Corel</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}